<template>
    <v-form v-if="showDocument" ref="entityEditForm">
        <v-container>
            <v-row>
                <v-col cols="12" md="4" order-md="3">
                    <div class="d-flex justify-center justify-md-end">
                        <action-buttons
                            :state="editState"
                            :permissions="userPermission"
                            :disabled="disableButtons"
                            @save="handleSave"
                            @cancel="handleCancel"
                            @close="handleClose"
                            :is-dirty="isDirty"
                            class="d-flex justify-end"
                        />
                        <v-btn
                            v-if="toursEnabled"
                            icon
                            small
                            @click="replayEditorTour"
                            title="Replay tour"
                            aria-label="Replay tour"
                            class="ml-1"
                        >
                            <v-icon small>mdi-help-circle-outline</v-icon>
                        </v-btn>
                    </div>
                </v-col>
                <v-col cols="12" md="4" order-md="1" v-if="!isNew">
                    <last-edit-details :entity="doc" />
                </v-col>
                <v-col cols="12" md="4" order-md="2">
                    <v-select
                        v-if="showTeamPicker"
                        :items="teams"
                        v-model="doc.teamId"
                        label="Select Team"
                        item-text="name"
                        item-value="id"
                        dense
                        solo
                        hint="Team this is assigned to"
                        persistent-hint
                    ></v-select>
                </v-col>
            </v-row>
            <v-row v-if="errorMessage" justify>
                <v-col>
                    <v-alert type="error">
                        {{ errorMessage }}
                    </v-alert>
                </v-col>
            </v-row>
            <slot name="editor" v-bind:doc="doc"></slot>
            <v-row>
                <v-col cols="12">
                    <div class="d-flex justify-center justify-md-end">
                        <action-buttons
                            :state="editState"
                            :permissions="userPermission"
                            :disabled="disableButtons"
                            @save="handleSave"
                            @cancel="handleCancel"
                            @close="handleClose"
                            :is-dirty="isDirty"
                            class="d-flex justify-end"
                        />
                    </div>
                </v-col>
            </v-row>
        </v-container>
    </v-form>
</template>
<script>
import api from '@/api'
import { mapState, mapGetters } from 'vuex'
import entities from './entities'
import { startEditorCloseTour, startEditorDirtyTour, resetEditorTours, TOURS_ENABLED } from '@/tour/index'
export default {
    name: 'EntityEditor',
    props: {
        entity: {
            type: String,
            required: true,
            validator: value => {
                return entities.includes(value)
            },
        },
        id: {
            type: [Number, String],
            required: false,
            validator: value => {
                return !!value
            },
        },
        apiName: {
            type: String,
            required: false,
        },
        copy: {
            type: Boolean,
            required: false,
            default: false,
        },
        newEntity: {
            type: Function,
            required: false,
        },
        readRouteName: {
            type: String,
            required: false,
        },
    },
    data() {
        return {
            isLoading: false,
            isSaving: false,
            isDirty: false,
            doc: null,
            errorMessage: '',
            requiredRule: v => !!v || 'Required',
            requiredRuleValidZero: v => v === 0 || !!v || 'Required',
        }
    },
    computed: {
        ...mapState(['user']),
        ...mapGetters(['events', 'nextEvent', 'userTeams']),
        toursEnabled() {
            return TOURS_ENABLED
        },
        userPermission() {
            if (this.user && this.user[this.entity]) {
                if (this.isNew) {
                    return this.user[this.entity].owned
                } else if (this.doc && this.doc.teamId) {
                    return this.user.teams.includes(this.doc.teamId)
                        ? this.user[this.entity].owned
                        : this.user[this.entity].shared
                }
                return 0
            } else {
                return 0
            }
        },
        showDocument() {
            return !this.isLoading && this.doc
        },
        apiEntityAddress() {
            return this.apiName || `${this.entity}s`
        },
        disableButtons() {
            return this.isLoading || this.isSaving
        },
        isNew() {
            return !this.id || this.copy
        },
        editState() {
            return this.isNew ? 'Create' : 'Edit'
        },
        teamCount() {
            return this.user && this.user.teams ? this.user.teams.length : 1
        },
        showTeamPicker() {
            return this.teamCount > 1 || this.user[this.entity].all.some(x => x > 1)
        },
        userDefaultTeamId() {
            if (this.user && this.user.defaultTeam) {
                let teamId = parseInt(this.user.defaultTeam)
                if (!isNaN(teamId)) return teamId
            }
            return this.user && this.user.teams ? this.user.teams[0] : 0
        },
        teams() {
            return this.userTeams(this.entity)
        },
        capsEnt() {
            return this.entity[0].toUpperCase() + this.entity.substring(1)
        },
        readRoute() {
            return this.readRouteName ? this.readRouteName : `read${this.capsEnt}`
        },
    },
    watch: {
        id(newVal) {
            if (newVal) {
                this.getEntity()
            }
        },
        doc: {
            deep: true,
            handler(newVer, oldVer) {
                this.isDirty = newVer && oldVer ? newVer.version === oldVer.version : false
                this.$emit('isDirtyChange', this.isDirty)
            },
        },
        showDocument(val) {
            if (val && !this.isDirty) {
                // startEditorCloseTour returns early if already seen (guarded by localStorage)
                this.$nextTick(() => startEditorCloseTour())
            }
        },
        isDirty(val) {
            if (val) {
                // startEditorDirtyTour returns early if already seen (guarded by localStorage)
                this.$nextTick(() => startEditorDirtyTour())
            }
        },
    },
    mounted() {
        if (this.id) {
            this.getEntity(this.copy)
        } else {
            this.doc = this.newEntity()
        }
    },
    activated() {
        if (this.id && !this.isLoading) {
            this.doc = null
            this.getEntity(this.copy)
        }
    },
    methods: {
        getEntity(isCopy) {
            //Comparison on String value as that's what is passed from router
            if (this.doc && parseInt(this.doc.keyId) === this.id) return
            this.isLoading = true
            api.get(`/${this.apiEntityAddress}/${this.id}`)
                .then(res => {
                    if (isCopy) {
                        delete res.createdBy
                        delete res.createdDate
                        delete res.keyId
                        delete res.statusId
                        delete res.statusText
                        delete res.teamId
                        delete res.teamName
                        delete res.version
                    }
                    this.doc = res
                    this.$emit('entityLoaded', this.doc)
                })
                .finally(() => {
                    this.isLoading = false
                })
        },
        handleSave(isClose) {
            if (!this.$refs.entityEditForm.validate()) return
            this.isSaving = true
            if (!this.isNew) {
                api.put(`/${this.apiEntityAddress}/${this.id}`, this.doc)
                    .then(res => {
                        this.doc = res
                        this.showSnackbar('success', `${this.entity} Saved`)
                        if (isClose) {
                            this.handleClose()
                        }
                    })
                    .catch(res => {
                        if (res.err.response && res.err.response.status === 409) {
                            this.errorMessage = `${this.entity} NOT SAVED.  It has been updated elsewhere and this is no longer the most recent verions.  Please reload doc.  This will lose your changes`
                        }
                    })
                    .finally(() => {
                        this.isSaving = false
                    })
            } else {
                if (this.teamCount == 1) {
                    this.doc.teamId = this.userDefaultTeamId
                }
                api.post(`/${this.apiEntityAddress}`, this.doc)
                    .then(res => {
                        this.showSnackbar('success', `${this.entity} Created`)
                        this.$emit('isDirtyChange', false)
                        this.$router.push({ name: this.readRoute, params: { id: res } })
                    })
                    .finally(() => {
                        this.isSaving = false
                        if (isClose) {
                            this.handleClose()
                        }
                    })
            }
        },
        handleCancel() {
            if (this.isDirty) {
                this.$confirm({
                    title: 'Discard Changes',
                    body: 'Are you sure you want to discard your changes?',
                    confirmButtonText: 'Discard',
                    cancelButtonText: 'Keep Editing',
                })
                    .then(confirmed => {
                        if (confirmed) {
                            this.isDirty = false
                            this.$emit('isDirtyChange', false)
                            window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
                        }
                    })
                    .catch(() => {})
            } else {
                window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
            }
        },
        handleClose() {
            window.history.length > 1 ? this.$router.go(-1) : this.$router.push('/')
        },
        replayEditorTour() {
            resetEditorTours()
            this.$nextTick(() => {
                if (this.isDirty) {
                    startEditorDirtyTour()
                } else {
                    startEditorCloseTour()
                }
            })
        },
    },
}
</script>
